import { getHistoryActions } from "../stores/historyStore";
import { getProjectSaveActions, getProjectSaveState } from "../stores/projectSaveStore";
import { getSceneState } from "../stores/sceneStore";

const SCENE_REPLACEMENT_MESSAGE =
  "This will replace the current scene. Unsaved changes will be lost. Continue?";
export const SCENE_REPLACEMENT_CONFIRM_EVENT = "critter-scene-replacement-confirm";

export type SceneReplacementConfirmDetail = {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  handled: boolean;
  resolve: (confirmed: boolean) => void;
};

const hasRecordEntries = (value: unknown): boolean =>
  Boolean(
    value && typeof value === "object" && Object.keys(value as Record<string, unknown>).length > 0
  );

const hasArrayEntries = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

type SceneReplacementWorkOptions = {
  hasFileStoreWork?: boolean;
  currentSnapshotHash?: string | null;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

const normalizeWorkOptions = (
  optionsOrHasFileStoreWork: SceneReplacementWorkOptions | boolean = false
): SceneReplacementWorkOptions =>
  typeof optionsOrHasFileStoreWork === "boolean"
    ? { hasFileStoreWork: optionsOrHasFileStoreWork }
    : optionsOrHasFileStoreWork;

export const hasMeaningfulSceneWork = (): boolean => {
  const scene = getSceneState();
  return Boolean(
    hasRecordEntries(scene.bodies) ||
    hasArrayEntries(scene.worldBodies) ||
    hasRecordEntries(scene.joints) ||
    hasRecordEntries(scene.sites) ||
    hasRecordEntries(scene.cameras) ||
    hasRecordEntries(scene.meshes) ||
    hasRecordEntries(scene.hfields) ||
    hasRecordEntries(scene.tendons) ||
    hasRecordEntries(scene.actuators) ||
    hasRecordEntries(scene.sensors) ||
    hasRecordEntries(scene.equality) ||
    hasArrayEntries(scene.contactPairs) ||
    hasArrayEntries(scene.contactExcludes)
  );
};

export const hasReplaceableSceneWork = (
  optionsOrHasFileStoreWork: SceneReplacementWorkOptions | boolean = false
): boolean => {
  const options = normalizeWorkOptions(optionsOrHasFileStoreWork);
  const projectSaveActions = getProjectSaveActions();
  const hasMeaningfulProjectWork = hasMeaningfulSceneWork();
  if (
    projectSaveActions.hasUnsavedChanges(
      options.currentSnapshotHash ?? null,
      hasMeaningfulProjectWork
    )
  )
    return true;
  if (!hasMeaningfulProjectWork) return false;
  if (getProjectSaveState().lastSavedSnapshotHash) return false;

  if (options.hasFileStoreWork) return true;

  const historyActions = getHistoryActions();
  if (historyActions.canUndo() || historyActions.canRedo()) return true;

  return false;
};

export const hasBrowserUnloadBlockingWork = (currentSnapshotHash: string | null): boolean => {
  return getProjectSaveActions().hasUnsavedChanges(currentSnapshotHash, hasMeaningfulSceneWork());
};

export const confirmSceneReplacement = (
  optionsOrHasFileStoreWork: SceneReplacementWorkOptions | boolean = false
): boolean => {
  if (!hasReplaceableSceneWork(optionsOrHasFileStoreWork)) return true;
  const options = normalizeWorkOptions(optionsOrHasFileStoreWork);
  return window.confirm(options.message || SCENE_REPLACEMENT_MESSAGE);
};

export const requestSceneReplacementConfirmation = (
  optionsOrHasFileStoreWork: SceneReplacementWorkOptions | boolean = false
): Promise<boolean> => {
  if (!hasReplaceableSceneWork(optionsOrHasFileStoreWork)) return Promise.resolve(true);
  if (typeof window === "undefined") return Promise.resolve(true);
  const options = normalizeWorkOptions(optionsOrHasFileStoreWork);

  return new Promise((resolve) => {
    const detail: SceneReplacementConfirmDetail = {
      title: options.title || "Unsaved Changes",
      message: options.message || "Save before replacing this scene?",
      confirmLabel: options.confirmLabel || "Don't Save",
      cancelLabel: options.cancelLabel || "Cancel",
      handled: false,
      resolve,
    };
    window.dispatchEvent(new CustomEvent(SCENE_REPLACEMENT_CONFIRM_EVENT, { detail }));
    if (!detail.handled) resolve(confirmSceneReplacement(optionsOrHasFileStoreWork));
  });
};

export const markBeforeUnloadIfSceneWouldBeReplaced = (
  event: BeforeUnloadEvent,
  shouldBlockUnload: boolean
): void => {
  if (!shouldBlockUnload) return;
  event.preventDefault();
  event.returnValue = "";
};
